import React from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  setCurrentStep,
  markStepVisited,
} from "../../../store/features/shipBuilderSlice";
import { validateStepAccessibility } from "../../../utils/validation";
import { SHIP_BUILDER_STEPS, ShipBuilderStepId } from "../../../constants/ship";

/**
 * Component for navigating between ship building steps
 * Uses Redux for state management
 *
 * @component
 * @example
 * ```tsx
 * <StepNavigation />
 * ```
 */
export const StepNavigation: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentStep = useAppSelector((state) => state.shipBuilder.currentStep);
  const shipBuilderState = useAppSelector((state) => state.shipBuilder);

  const handleStepClick = (stepIndex: number) => {
    const stepId = SHIP_BUILDER_STEPS[stepIndex].id as ShipBuilderStepId;
    const validation = validateStepAccessibility(shipBuilderState, stepId);

    if (validation.isValid) {
      dispatch(setCurrentStep(stepIndex));
      dispatch(markStepVisited(stepIndex));
    }
  };

  // Add defensive programming to handle undefined visitedSteps
  const visitedSteps = shipBuilderState.visitedSteps || [];

  return (
    <div className="flex flex-wrap gap-2 mb-6">
      {SHIP_BUILDER_STEPS.map((step, index) => {
        const stepId = step.id as ShipBuilderStepId;
        const validation = validateStepAccessibility(shipBuilderState, stepId);
        const isCurrent = index === currentStep;
        const isVisited = visitedSteps.includes(index);
        const isValid = validation.isValid;

        return (
          <button
            key={step.id}
            onClick={() => handleStepClick(index)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isCurrent
                ? "bg-blue-600 text-white"
                : isVisited
                  ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border border-green-300 dark:border-green-700"
                  : isValid
                    ? "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-700"
            }`}
            disabled={!isValid}
          >
            {step.label}
          </button>
        );
      })}
    </div>
  );
};
